import React, { useCallback, useMemo } from 'react'
import { useStore } from '../../../store/store';
import { ItemTypes, VARIABLE_COLOR } from '../../../constants/constants';
import { convertContentToRawText, getBlockStyle } from '../../RichEditor/utils';
import { App, Popover } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { transformTextToEditorState } from '../../../utils/dataSync';
import { Editor } from 'draft-js';
import { useStyleMap } from '../../../hooks';
import { isATitleBox } from '../../../utils/boxes';
import { displayTitleIndex, getTitlesConfig, numberedTitleLevels, titleDisplayIndex } from '../../../utils/titles';
import { useTranslation } from 'react-i18next';
import ItemRefresher from './ItemRefresher';
import PredefinedValuesPicker from './PredefinedValuesPicker';
import ImagePredefinedValuesPicker from './ImagePredefinedValuesPicker';
import TextPredefinedValuesPicker from './TextPredefinedValuesPicker';

const ItemName = ({ variableBox, isLast, hasVariableSelected, formItem, zoom }) => {
  const styleMap = useStyleMap()
  const { t } = useTranslation()
  // `App.useApp()` et non `Modal.confirm` : la version statique rend hors de
  // l'arbre React, donc hors du ConfigProvider, et garde le zIndexPopupBase par
  // défaut — la modale repasserait sous la barre flottante (cf. Layout.jsx).
  const { modal: modalApi } = App.useApp()
  const boxes = useStore((state) => state.boxes)
  const isTemplate = useStore(({ currentDocument }) => currentDocument.type === 'template')
  const defaultVariablesApi = useStore(({ defaultVariablesApi }) => defaultVariablesApi)
  const isAddress = useStore(({ variables }) => !!variables[variableBox.id]?.derived)
  const titleBoxesIndex = useStore((state) => state.boxes.reduce((filtered, box, index) => {
    if (isATitleBox(box.type)) {
      filtered.push({
        index, title: `${displayTitleIndex(
          titleDisplayIndex(box.content),
          state.configuration.separatorTitle,
          getTitlesConfig(state.configuration),
          numberedTitleLevels(state.configuration),
          state.configuration.finalSeparatorTitle
        )} ${box.content.editorState.getCurrentContent().getPlainText()}`
      });
    }
    return filtered;
  }, []));

  const textColor = hasVariableSelected ? VARIABLE_COLOR : "#b1b1b1";

  const getTitle = (v) => {
    const baseTitle = t('remarks')
    const vIndex = boxes.findIndex((b) => b.id === v.id);
    if (titleBoxesIndex.length < 1 ||
      titleBoxesIndex.every(({ index }) => vIndex < index)) {
      return `${baseTitle} :`;
    }
    return `${titleBoxesIndex.find(({ index }, currentIndex) => {
      return vIndex > index && (
        currentIndex + 1 === titleBoxesIndex.length ||
        vIndex < titleBoxesIndex[currentIndex + 1].index
      )
    }).title} :`
  }

  const reloadRecursiveSectionByModel = useStore(({ reloadRecursiveSectionByModel }) => reloadRecursiveSectionByModel)

  const recursiveModelName = useMemo(() => {
    if (variableBox.type !== ItemTypes.VARIABLE_GROUP) return null;
    const firstId = formItem?.variablesBoxes?.[0]?.id ?? '';
    const parts = firstId.split('.');
    if (parts.length === 3 && !isNaN(parts[1])) return parts[0];
    return null;
  }, [variableBox.type, formItem])

  const handleRecursiveRefresh = useCallback(() => {
    const modelLabel = recursiveModelName ?? 'ce modèle';
    modalApi.confirm({
      title: 'Recharger les variables',
      content: `Cette action va recharger toutes les variables "${modelLabel}" depuis le back-office. Les modifications manuelles seront perdues.`,
      okText: 'Recharger',
      cancelText: 'Annuler',
      onOk: () => reloadRecursiveSectionByModel(recursiveModelName),
      centered: true,
    });
  }, [modalApi, recursiveModelName, reloadRecursiveSectionByModel])

  // Variables d'un groupement à comparer à l'API et recharger. Une adresse
  // découpée l'est par ses parties : recharger une partie recompose l'agrégat,
  // alors que l'agrégat de l'API (full_address) peut différer en forme seule.
  const groupRefreshableBoxes = useMemo(() => variableBox.type === ItemTypes.VARIABLE_GROUP
    ? formItem.variablesBoxes.filter((v) => !v.notUsed).flatMap((v) => v.addressParts ?? [v])
    : [], [variableBox.type, formItem])

  const canBeUpdated = useMemo(() => {
    if (isTemplate) return false
    const isVariableValueDifferent = (variable) => {
      const keys = variable.id.split('.')

      if (keys.length !== 2 && keys.length !== 3) return false

      const categoryKey = keys.length === 3 ? keys[1] : keys[0]

      let value;

      const informationKey = keys.length === 3 ? keys[2] : keys[1]

      if (keys.length === 3 && !isNaN(keys[1])) {
        // recursive indexed key (e.g. material.0.name) — compare against apiValue
        return variable.apiValue !== undefined && variable.value !== variable.apiValue
      }

      if (keys.length === 3) {
        const indexKey = defaultVariablesApi.opportunities_variables?.findIndex((opp) => opp[Object.keys(opp)[0]].opportunity.CHRONO === keys[0])
        if (indexKey == null || indexKey === -1) return false
        const opportunityKey = Object.keys(defaultVariablesApi.opportunities_variables[indexKey])[0]
        value = defaultVariablesApi.opportunities_variables[indexKey][opportunityKey][categoryKey]?.[informationKey] ?? ''
      } else if (categoryKey === 'custom') {
        value = defaultVariablesApi.custom_text_variables[`${variable.name}.${informationKey}`] ?? ''
      }
      else if (defaultVariablesApi.offer_variables) {
        value = defaultVariablesApi.offer_variables[categoryKey]?.[informationKey] ?? ''
      } else {
        value = defaultVariablesApi[categoryKey]?.[informationKey]
      }
      if (value !== '' && value !== variable.value) {
        return true
      }
      return false
    }
    if (variableBox.type === ItemTypes.CUSTOM_TEXT_VARIABLE) {
      return (isVariableValueDifferent(variableBox))
    }
    if (variableBox.type === ItemTypes.VARIABLE_GROUP) {
      return (groupRefreshableBoxes.some((variable) => isVariableValueDifferent(variable)))
    }
    return false
  }, [variableBox, defaultVariablesApi, groupRefreshableBoxes, isTemplate])

  const getName = () => {
    // Une adresse découpée s'affiche en groupement (ses parties) mais reste une
    // variable qu'on insère dans le document : elle garde son « @ ».
    if (variableBox.type === ItemTypes.CUSTOM_TEXT_VARIABLE || isAddress) {
      return `@${variableBox.name}`
    }
    return variableBox.name
  }

  return <span key={variableBox.id} style={{
    display: 'flex',
    alignItems: 'center',
    minHeight: 25
  }}>
    <span style={{ color: textColor }}>{typeof variableBox.name === "string" ? getName() : <Editor
      readOnly
      editorState={getName()} />}</span>
    {variableBox.remark && convertContentToRawText(transformTextToEditorState(variableBox.remark)).trim() !== '' &&
      <Popover
        placement='top'
        mouseLeaveDelay='0'
        content={<Editor
          stripPastedStyles={true}
          readOnly
          editorState={transformTextToEditorState(variableBox.remark)}
          customStyleMap={styleMap}
          blockStyleFn={getBlockStyle}
          className='RichEditor-editor'
          style={{
            overflow: 'visible',
          }}
        />}
        overlayStyle={{
          zoom: `${zoom}%`
        }}
        title={getTitle(variableBox)}
      >
        {" "}
        <FontAwesomeIcon
          icon={icon({ name: 'circle-info', style: 'solid' })}
          style={{ marginLeft: 2, color: "#5499DE" }}
        />
      </Popover>
    }
    {/* Valeurs prédéfinies : uniquement en proposition, le champ de saisie
        étant désactivé en mode modèle. */}
    {!isTemplate && variableBox.type === ItemTypes.CUSTOM_TEXT_VARIABLE &&
      <PredefinedValuesPicker variableKey={variableBox.id} zoom={zoom} />}
    {!isTemplate && variableBox.type === ItemTypes.IMG_VARIABLE &&
      <ImagePredefinedValuesPicker boxId={variableBox.id} zoom={zoom} />}
    {!isTemplate && variableBox.type === ItemTypes.TEXT_VARIABLE &&
      <TextPredefinedValuesPicker boxId={variableBox.id} zoom={zoom} />}
    {canBeUpdated && <span><ItemRefresher
      variableIds={
        variableBox.type === ItemTypes.VARIABLE_GROUP ? groupRefreshableBoxes.map(({ id }) => id) :
          [variableBox.id]
      }
      onRefresh={recursiveModelName ? handleRecursiveRefresh : undefined}
    /></span>}
    <span style={{ color: "#b1b1b1" }}>{isLast ? "" : " - "}</span>
  </span>
}

export default ItemName
